// Desktop storage: in the Tauri shell the webview talks to native SQLite through Rust commands
// (apps/desktop/src-tauri/src/db.rs). Same StorageAdapter contract, same changeset semantics and
// DDL as the Node SqliteAdapter, so the conformance suite describes both.
import type { Changeset, Dataset } from '@vero/domain';
import { fromBase64, toBase64, type FileMeta, type SnapshotMeta, type StorageAdapter } from '@vero/store';

type Invoke = <T>(cmd: string, args?: Record<string, unknown>) => Promise<T>;

export function tauriInvoke(): Invoke | null {
  const w = window as unknown as { __TAURI__?: { core?: { invoke: Invoke } } };
  return w.__TAURI__?.core?.invoke ?? null;
}

export class TauriSqliteAdapter implements StorageAdapter {
  readonly engine = 'sqlite (native)';
  private invoke: Invoke;
  constructor(invoke: Invoke) { this.invoke = invoke; }
  open() { return this.invoke<{ schemaVersion: number; created: boolean }>('db_open'); }
  loadAll() { return this.invoke<Dataset>('db_load_all'); }
  commit(cs: Changeset) { return this.invoke<void>('db_commit', { changeset: cs }); }
  replaceAll(ds: Dataset) { return this.invoke<void>('db_replace_all', { dataset: ds }); }
  getMeta<T>(key: string) { return this.invoke<T | undefined>('db_get_meta', { key }); }
  setMeta(key: string, value: unknown) { return this.invoke<void>('db_set_meta', { key, value }); }
  saveSnapshot(meta: SnapshotMeta, content: string) { return this.invoke<void>('db_save_snapshot', { meta, content }); }
  listSnapshots() { return this.invoke<SnapshotMeta[]>('db_list_snapshots'); }
  readSnapshot(id: string) { return this.invoke<string | undefined>('db_read_snapshot', { id }); }
  deleteSnapshot(id: string) { return this.invoke<void>('db_delete_snapshot', { id }); }
  async putFile(meta: FileMeta, content: Uint8Array) { return this.invoke<void>('db_put_file', { meta, content: toBase64(content) }); }
  async getFile(id: string) { const r = await this.invoke<{ meta: FileMeta; content: string } | null>('db_get_file', { id }); return r ? { meta: r.meta, content: fromBase64(r.content) } : undefined; }
  deleteFile(id: string) { return this.invoke<void>('db_delete_file', { id }); }
  listFiles() { return this.invoke<FileMeta[]>('db_list_files'); }
  async close() {}
}
